/* eslint-disable react/no-danger */
/* eslint-disable no-plusplus */
import React, { useMemo, useState } from 'react';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { FormattedMessage, injectIntl } from 'react-intl';
import { withRouter } from 'react-router-dom';
import { get } from 'lodash';
import { Form as FinalForm, Field } from 'react-final-form';
import moment from 'moment';
import format from 'date-fns/format';
import Decimal from 'decimal.js';
import { AddOnBlock, Heading, Box, FeatureListItem } from '@landtrustinc/design-system';
import config from '../../config';
import { types as sdkTypes } from '../../util/sdkLoader';
import { createSlug } from '../../util/urlHelpers';
import { ensureListing, ensureUser } from '../../util/data';
import { propTypes } from '../../util/types';
import { createResourceLocatorString, findRouteByRouteName } from '../../util/routes';
import { composeValidators, required } from '../../util/validators';
import { getMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import { initializeCardPaymentData } from '../../ducks/stripe.duck';
import EstimatedBreakdownMaybe from '../../forms/BookingDatesForm/EstimatedBreakdownMaybe';
import routeConfiguration from '../../routeConfiguration';
import {
  formatPricingFormValues,
  getFlexiblePricingData,
  getPackageLodgingPrices,
  handleLodgingAddOnChange,
  isFlexiblePricingEnabled,
} from '../../util/package';
import { getPackageScarcityMessage } from '../../components/PackagePricingPanel/components/PackagePricingForm';
import PackageNotFoundPage from '../PackageNotFoundPage/PackageNotFoundPage';
import { getPackage, getLodgingTax, getLodgingPricesOptions } from '../../util/listing';
import { getLodgingIcon } from '../../util/lodging';
import {
  CredovaPlugin,
  Form,
  FieldDates,
  Logo,
  TransactionUserCard,
  NamedLink,
  NamedRedirect,
  Page,
  PrimaryButton,
  FieldPartySizeSelect,
  FlexiblePartySizeSelect,
  PackagesBreadcrumbs,
  FormValidationTooltip,
  PackageBadges,
  PackageItinerary,
  Banner,
} from '../../components';
import { daysAdded, getDaysFromTimeslots } from '../../util/dates';

import css from './PricingPage.module.css';

const { UUID, Money } = sdkTypes;

const PricingPageComponent = ({
  history,
  intl,
  listing,
  isListingLoading,
  fetchTimeSlotsError,
  params: rawParams,
  timeSlots,
  unitType,
  callSetInitialValues,
  currentUser,
  onInitializeCardPaymentData,
  params,
}) => {
  const availableDays = getDaysFromTimeslots(timeSlots);
  const currentListing = ensureListing(listing);
  const currentAuthor = ensureUser(currentListing.author);
  const [estimatedTotalPrice, setEstimatedTotalPrice] = useState(0);

  const isOwnListing =
    currentUser &&
    currentUser.id &&
    currentAuthor &&
    currentAuthor.id &&
    currentAuthor.id.uuid === currentUser.id.uuid;

  const listingTitle = currentListing.attributes.title;
  const publicData = get(currentListing, 'attributes.publicData');
  const partySize = get(publicData, 'policies.sportsmen', 1);
  const packages = get(publicData, 'packages', []);
  const packageInfo = packages.find(item => item.id === params.packageId);
  // guest/party size info
  const packageMinParty = get(packageInfo, 'guests.min', 1);
  const packageMaxParty = get(packageInfo, 'guests.max', 1);
  const flexiblePricingData = getFlexiblePricingData(packageInfo?.flexiblePricing, packageMinParty);

  const initialValues = useMemo(() => {
    if (packageInfo && isFlexiblePricingEnabled(packageInfo.activity)) {
      const initialPartySize =
        packageInfo?.guests?.max === 0
          ? 2 // adult non hunter + youth hunter
          : flexiblePricingData.adultHunter.quantity;

      const flexibleGuestSize =
        packageInfo?.guests?.max === 0
          ? {
              youthHunter: 1,
              adultNonHunter: 1,
            }
          : {
              adultHunter: packageMinParty || 1,
            };

      return {
        partySize: {
          partySize: initialPartySize,
          flexibleGuestSize,
        },
      };
    }

    return {};
  }, [packageInfo, flexiblePricingData.adultHunter.quantity, packageMinParty]);

  // Redirect back to ListingPage if its the own listing
  // Redirection must happen before any data format error is thrown (e.g. wrong currency)
  if (isOwnListing) {
    return <NamedRedirect name="ListingPage" params={params} />;
  }

  const hasPackage = !!packageInfo;

  if (!packageInfo || !packageInfo.isPublished) {
    return <PackageNotFoundPage />;
  }

  const { hasLodging } = packageInfo || {};

  const diyPartySizeMax = [];
  for (let i = 1; i <= partySize; i++) {
    diyPartySizeMax.push(i);
  }
  const { price: listingPrice } = currentListing.attributes;

  const title = intl.formatMessage({ id: 'PricingPage.title' }, { listingTitle });

  const pageProps = { title, scrollingDisabled: false };

  const topbar = (
    <div className={css.topbar}>
      <NamedLink className={css.home} name="LandingPage">
        <Logo
          className={css.logoMobile}
          title={intl.formatMessage({ id: 'CheckoutPage.goToLandingPage' })}
          format="desktop"
        />
        <Logo
          className={css.logoDesktop}
          alt={intl.formatMessage({ id: 'CheckoutPage.goToLandingPage' })}
          format="desktop"
        />
      </NamedLink>
    </div>
  );

  if (isListingLoading) {
    return <Page {...pageProps}>{topbar}</Page>;
  }

  const currentTransaction = get(history, 'location.state.currentTransaction', null);

  const handleFormSubmit = values => {
    const { initialValues: formattedInitialValues, saveToSessionStorage } = formatPricingFormValues(
      values,
      packageMinParty,
      listing,
      hasLodging,
      currentUser
    );

    const routes = routeConfiguration();

    // Customize checkout page state with current listing and selected bookingDates
    const { setInitialValues } = findRouteByRouteName('CheckoutPage', routes);
    callSetInitialValues(setInitialValues, formattedInitialValues, saveToSessionStorage);
    // Clear previous Stripe errors from store if there is any
    onInitializeCardPaymentData();

    const nextRoute = createResourceLocatorString(
      'CheckoutPackagePage',
      routes,
      {
        id: listing.id.uuid,
        packageId: packageInfo.id,
        slug: createSlug(listing.attributes.title),
      },
      {}
    );

    history.push(nextRoute, { currentTransaction });
  };

  const handlePriceChange = price => {
    setEstimatedTotalPrice(price);
  };

  return (
    <Page {...pageProps} noindex nofollow>
      {topbar}

      <div className={css.contentContainer}>
        <div className={css.mobileBreadcrumbs}>
          <PackagesBreadcrumbs params={params} listing={listing} step="pricing" />
        </div>

        <div className={css.mobileUserCard}>
          <TransactionUserCard
            listing={listing}
            packageInfo={packageInfo}
            currentUser={currentUser}
          />
        </div>

        <FinalForm
          unitPrice={listingPrice}
          onSubmit={handleFormSubmit}
          initialValues={initialValues}
          render={formRenderProps => {
            const {
              handleSubmit,
              form,
              values,
              unitPrice,
              inProgress,
              invalid,
              submitError,
            } = formRenderProps;

            let bookingDateText = hasPackage ? (
              <FormattedMessage id="CheckoutPage.datesFieldPlaceholderPackage" />
            ) : (
              <FormattedMessage id="CheckoutPage.datesFieldPlaceholder" />
            );
            const dateValues = values && values.dates;

            const isSingleDay =
              daysAdded(dateValues && dateValues.startDate, dateValues && dateValues.endDate) === 1;

            if (dateValues) {
              bookingDateText = `${format(dateValues.startDate, 'MMM DD')} - ${format(
                dateValues.endDate,
                'MMM DD, YYYY'
              )}`;
            }
            if (dateValues && isSingleDay) {
              bookingDateText = format(dateValues.startDate, 'MMM DD');
            }

            const { startDate, endDate, startLodgingDate } = dateValues ?? {};
            const scarcityMessage = getPackageScarcityMessage(packageInfo);

            const { days } = packageInfo || {};

            const formattedStartDate = moment(startDate).format('YYYY-MM-DD');
            const formattedEndDate = moment(endDate).format('YYYY-MM-DD');
            const additionalItems = get(values, 'additionalItems', []);

            let submitDisabled;

            if (hasPackage) {
              submitDisabled = invalid || inProgress || !startDate || !endDate;
            } else {
              submitDisabled =
                invalid || inProgress || !startDate || !endDate || !additionalItems.length;
            }

            let packageItem = null;

            const lodgingPricesOptions = getLodgingPricesOptions(currentListing, intl);

            const currentPackage = getPackage(currentListing, rawParams.packageId);

            const { optionalLodging, includedLodging } = getPackageLodgingPrices(
              currentPackage,
              lodgingPricesOptions
            );

            let lodgingFees = (values.lodgingAddons || []).map(addon => {
              const lodgingPrice = JSON.parse(optionalLodging[addon].price, sdkTypes.reviver);

              return {
                price: lodgingPrice,
                type: optionalLodging[addon].type,
              };
            });

            if (includedLodging?.length) {
              lodgingFees = lodgingFees.concat(
                includedLodging.map(item => {
                  const lodgingPrice = JSON.parse(item.price, sdkTypes.reviver);

                  return {
                    price: lodgingPrice,
                    type: item.type,
                  };
                })
              );
            }

            const includeLodgings = values?.lodgingAddons?.length > 0 || hasLodging;

            const packagePrice = JSON.parse(packageInfo.price);

            const packageFee = new Money(packagePrice.amount, config.currency);
            const today = moment();
            const minDaysEndDate = moment().add(days?.min - 1 || 0, 'd');
            // Handle both standard party size and flexible pricing data
            let partySizeMaybe = packageMinParty;
            let flexiblePricingBookingData = null;

            if (values.partySize) {
              if (typeof values.partySize === 'object' && values.partySize.partySize) {
                // Flexible pricing data
                partySizeMaybe = parseInt(values.partySize.partySize, 10);
                flexiblePricingBookingData = values.partySize.flexibleGuestSize;
              } else {
                // Standard party size
                partySizeMaybe = parseInt(values.partySize, 10);
              }
            }

            packageItem = {
              code: `line-item/${packageInfo.title.replace(/\s/g, '-')}`,
              title: packageInfo.title,
              includeFor: ['customer', 'provider'],
              unitPrice: packageFee,
              quantity: new Decimal(1),
              lineTotal: packageFee,
              reversal: false,
              guestSize: partySizeMaybe,
              activity: packageInfo.activity,
              hasLodging: includeLodgings,
              lodgingFees,
            };

            const lodgingTax = getLodgingTax(listing, packageInfo?.id);

            const bookingData = {
              unitType,
              unitPrice,
              startDate: startDate || today,
              endDate: endDate || minDaysEndDate,
              startLodgingDate,
              quantity: 1,
              partySize: partySizeMaybe,
              packageItem,
              lodgingTax: includeLodgings && lodgingTax,
              isInitial: !dateValues,
              flexiblePricing: flexiblePricingBookingData,
            };
            const timeSlotsError = fetchTimeSlotsError ? (
              <p className={css.error}>
                <FormattedMessage id="PricingPage.timeSlotsError" />
              </p>
            ) : null;

            let calendarInfoDays;

            if (days.min === days.max) {
              calendarInfoDays = days.min;
            } else if (days.max === 365) {
              calendarInfoDays = `${days.min}+`;
            } else {
              calendarInfoDays = `${days.min}-${days.max}`;
            }

            const showLodgingAddOn =
              optionalLodging?.length > 0 && (packageInfo.days.min > 1 || !isSingleDay);

            return (
              <>
                <div className={css.detailsWrapper}>
                  <div>
                    <div className={css.desktopBreadcrumbs}>
                      <PackagesBreadcrumbs
                        params={params}
                        history={history}
                        listing={listing}
                        step="pricing"
                      />
                    </div>

                    <div className={css.packageIncludes}>
                      <div className={css.packageIncludesHeading}>
                        <FormattedMessage id="PackageCard.includes" />
                      </div>
                      <PackageBadges packageInfo={packageInfo} listing={currentListing} />
                    </div>

                    <div className={css.formWrapper}>
                      <Form onSubmit={handleSubmit}>
                        {timeSlotsError}
                        <div className={css.formFieldWrapper}>
                          {hasPackage && (
                            <div>
                              {packageMinParty > 1 && (
                                <div className={css.minPartyDisclaimer}>
                                  <FormattedMessage
                                    id="PricingPage.minimumPartySize"
                                    values={{ min: packageMinParty }}
                                  />
                                </div>
                              )}
                              <div className={css.partySizeWrapper}>
                                {isFlexiblePricingEnabled(packageInfo.activity) ? (
                                  <FlexiblePartySizeSelect
                                    className={css.fieldPartySize}
                                    name="partySize"
                                    activity={packageInfo.activity}
                                    minPartySize={packageMinParty}
                                    maxPartySize={packageMaxParty}
                                    price={packageInfo.price}
                                    packageFlexiblePricing={packageInfo.flexiblePricing}
                                    validate={composeValidators(
                                      required(
                                        intl.formatMessage({ id: 'PricingPage.partySizeRequired' })
                                      )
                                    )}
                                    readOnlyInput
                                    label={<FormattedMessage id="PricingPage.partySizeLabel" />}
                                  />
                                ) : (
                                  <FieldPartySizeSelect
                                    id="partySize"
                                    className={css.fieldPartySize}
                                    name="partySize"
                                    minPartySize={packageMinParty}
                                    maxPartySize={packageMaxParty}
                                    price={JSON.parse(packageInfo.price)}
                                    parse={value => (value === '' ? null : Number(value))}
                                    format={value => String(value)}
                                    validate={composeValidators(
                                      required(
                                        intl.formatMessage({ id: 'PricingPage.partySizeRequired' })
                                      )
                                    )}
                                    label={<FormattedMessage id="PricingPage.partySizeLabel" />}
                                  />
                                )}
                              </div>
                            </div>
                          )}

                          <div className={css.formFieldWrapper}>
                            <FieldDates
                              fieldDateProps={{
                                endDateOffset:
                                  packageInfo.days.min === packageInfo.days.max
                                    ? day => day.add(packageInfo.days.min - 1, 'days')
                                    : undefined,
                                minimumNights:
                                  packageInfo.days.min !== packageInfo.days.max &&
                                  packageInfo.days.min - 1,
                                maximumNights:
                                  packageInfo.days.max > 0 &&
                                  packageInfo.days.min !== packageInfo.days.max
                                    ? packageInfo.days.max - 1
                                    : undefined,
                                calendarInfo: (
                                  <FormattedMessage
                                    id="PricingPage.datesFieldInfoPackageTitle"
                                    values={{ days: calendarInfoDays }}
                                  />
                                ),
                                hasLodging: hasLodging || packageItem.hasLodging,
                                activity: packageInfo.activity,
                              }}
                              timeSlots={timeSlots}
                              label={<FormattedMessage id="PricingPage.datesFieldHeadingPackage" />}
                              text={bookingDateText}
                              onChange={dates => {
                                form.change('dates', dates);
                              }}
                              isPackage
                            />
                            {startDate && endDate && (
                              <div className={css.availableDates}>
                                <FormattedMessage
                                  id="InquiryWizard.selectedDate"
                                  values={{
                                    dates: formattedStartDate === formattedEndDate ? 1 : 0,
                                  }}
                                />
                              </div>
                            )}
                          </div>

                          {startDate && endDate && (
                            <PackageItinerary
                              activity={packageInfo.activity}
                              listing={listing}
                              startDate={moment(startDate).format('YYYY-MM-DD')}
                              endDate={moment(endDate).format('YYYY-MM-DD')}
                              hasLodging={hasLodging || packageItem.hasLodging}
                              startLodgingDate={
                                startLodgingDate
                                  ? moment(startLodgingDate).format('YYYY-MM-DD')
                                  : null
                              }
                            />
                          )}

                          {showLodgingAddOn && (
                            <div className={css.formFieldWrapper}>
                              <div className={css.lodgingContainer}>
                                <Box mb="var(--spacing-3)">
                                  <Heading size="2xs" fontWeight="bold">
                                    <FormattedMessage id="PricingPage.addOnsTitle" />
                                  </Heading>
                                </Box>

                                <Box display="flex" flexDirection="column" gap="var(--spacing-2)">
                                  {optionalLodging.map((item, index) => {
                                    const uniqueKey = `${item.type}-${item.index || index}`;
                                    return (
                                      <Field
                                        key={uniqueKey}
                                        name="lodgingAddons"
                                        type="checkbox"
                                        value={index}
                                      >
                                        {({ input }) => {
                                          return (
                                            <AddOnBlock
                                              title={item.shortLabel}
                                              subtitle={`+ ${item.priceLabel}`}
                                              icon={getLodgingIcon(item.type)}
                                              checked={input.checked}
                                              onClick={() => {
                                                const updatedAddons = handleLodgingAddOnChange(
                                                  index,
                                                  values.lodgingAddons || [],
                                                  dateValues,
                                                  availableDays,
                                                  hasLodging,
                                                  form.change
                                                );
                                                form.change('lodgingAddons', updatedAddons);
                                              }}
                                            />
                                          );
                                        }}
                                      </Field>
                                    );
                                  })}
                                </Box>
                              </div>
                            </div>
                          )}
                        </div>

                        <div className={css.mobileUserCard}>
                          {bookingData && (
                            <EstimatedBreakdownMaybe
                              bookingData={bookingData}
                              listing={listing}
                              onPriceChange={handlePriceChange}
                              hasLodging={hasLodging}
                              packageInfo={packageInfo}
                            />
                          )}
                        </div>

                        <div className={css.desktopUserCard}>
                          <TransactionUserCard
                            listing={listing}
                            breakdown={
                              bookingData && (
                                <EstimatedBreakdownMaybe
                                  bookingData={bookingData}
                                  listing={listing}
                                  onPriceChange={handlePriceChange}
                                  hasLodging={hasLodging}
                                  packageInfo={packageInfo}
                                />
                              )
                            }
                            packageInfo={packageInfo}
                            currentUser={currentUser}
                          />
                        </div>

                        <CredovaPlugin price={estimatedTotalPrice} />

                        <div className={css.submitButtonWrapper}>
                          {!startDate || !endDate ? (
                            <FormValidationTooltip
                              placement="top"
                              body={<FormattedMessage id="PricingPage.fieldRequirements" />}
                              title={<FormattedMessage id="PricingPage.fieldRequirementsTitle" />}
                            >
                              <PrimaryButton
                                isFullWidth
                                type="submit"
                                inProgress={inProgress}
                                disabled={submitDisabled}
                              >
                                <FormattedMessage id="PricingPage.continueToPayment" />
                              </PrimaryButton>
                            </FormValidationTooltip>
                          ) : (
                            <PrimaryButton
                              isFullWidth
                              type="submit"
                              inProgress={inProgress}
                              disabled={submitDisabled}
                            >
                              <FormattedMessage id="PricingPage.continueToPayment" />
                            </PrimaryButton>
                          )}

                          {scarcityMessage ? (
                            <div className={css.scarcityMessage}>
                              <FeatureListItem
                                className={css.scarcityFeatureListItem}
                                labelSize="sm"
                                label={
                                  <span
                                    className={
                                      scarcityMessage.isUrgent
                                        ? `${css.scarcityFeatureLabel} ${css.scarcityFeatureLabelUrgent}`
                                        : css.scarcityFeatureLabel
                                    }
                                  >
                                    <FormattedMessage
                                      id={scarcityMessage.id}
                                      values={scarcityMessage.values}
                                    />
                                  </span>
                                }
                              />
                            </div>
                          ) : null}

                          <div className={css.bannerContainer}>
                            <Banner
                              header={intl.formatMessage({ id: 'PricingDetails.bannerHeader' })}
                              body={intl.formatMessage({ id: 'PricingDetails.bannerText' })}
                            />
                          </div>
                        </div>

                        {submitError ? <div className={css.error}>{submitError}</div> : null}
                      </Form>
                    </div>
                  </div>
                </div>
              </>
            );
          }}
        />
      </div>
    </Page>
  );
};

const mapStateToProps = (state, ownProps) => {
  const {
    showListingInProgress,
    showListingInitial,
    showListingError,
    timeSlots,
    fetchTimeSlotsError,
  } = state.ListingPage;

  const { currentUser } = state.user;

  const { params } = ownProps;

  const getListing = id => {
    const ref = { id, type: 'listing' };
    const listings = getMarketplaceEntities(state, [ref]);

    return listings.length === 1 ? listings[0] : null;
  };

  const listingId = new UUID(params.id);
  const listing = getListing(listingId);

  return {
    currentUser,
    listing,
    isListingLoading: showListingInitial || showListingInProgress,
    showListingError,
    timeSlots,
    fetchTimeSlotsError,
  };
};

const mapDispatchToProps = dispatch => ({
  callSetInitialValues: (setInitialValues, values, saveToSessionStorage) =>
    dispatch(setInitialValues(values, saveToSessionStorage)),
  onInitializeCardPaymentData: () => dispatch(initializeCardPaymentData()),
});

PricingPageComponent.defaultProps = {
  unitType: config.bookingUnitType,
};

PricingPageComponent.propTypes = {
  unitType: propTypes.bookingUnitType,
};

const PricingPage = compose(
  withRouter,
  connect(
    mapStateToProps,
    mapDispatchToProps
  ),
  injectIntl
)(PricingPageComponent);

export default PricingPage;
